import React, { useEffect, useRef } from 'react';
import { createChart, IChartApi, ISeriesApi, CandlestickSeries, HistogramSeries } from 'lightweight-charts';

export interface KlineData {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface SignalData {
  direction: string;
  price: number;
  stop_loss: number;
  take_profit: number;
  confidence: number;
  strategy: string;
  timestamp: number;
  metadata?: Record<string, any>;
}

interface ScoreBreakdown {
  total: number;
  bb_width: number;
  volume_lull: number;
  atr_comp: number;
  oi_growth: number;
  funding: number;
  range_high: number;
  range_low: number;
}

interface MiniChartProps {
  symbol: string;
  klines: KlineData[];
  signal?: SignalData | null;
  lastPrice: number;
  onClick: () => void;
  phase?: string;
  indicators?: Record<string, any>;
  volumeRank?: number;
  turnover24h?: number;
  squeezeScore?: number;
  scoreBreakdown?: ScoreBreakdown;
  fundingRate?: number;
}

const formatCompact = (v: number): string => {
  if (v >= 1e9) return (v / 1e9).toFixed(2) + 'B';
  if (v >= 1e6) return (v / 1e6).toFixed(1) + 'M';
  if (v >= 1e3) return (v / 1e3).toFixed(1) + 'K';
  return v.toFixed(1);
};

// Determine decimal precision based on price magnitude
const pricePrecision = (price: number): number => {
  if (price >= 1000) return 2;
  if (price >= 1) return 2;
  if (price >= 0.01) return 4;
  if (price >= 0.0001) return 6;
  return 8;
};

// Filter invalid klines and deduplicate by timestamp (keep last)
const sanitizeKlines = (raw: KlineData[]): KlineData[] => {
  const map = new Map<number, KlineData>();
  for (const k of raw) {
    if (k.time > 0 && k.open > 0 && k.high > 0 && k.low > 0 && k.close > 0) {
      map.set(k.time, k);
    }
  }
  return Array.from(map.values()).sort((a, b) => a.time - b.time);
};

const MiniChart: React.FC<MiniChartProps> = ({
  symbol, klines, signal, lastPrice, onClick,
  phase, indicators, volumeRank, turnover24h,
  squeezeScore, scoreBreakdown, fundingRate,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const volRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const fittedRef = useRef(false);

  // Create chart ONCE on mount
  useEffect(() => {
    if (!containerRef.current) return;

    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height: 180,
      layout: {
        background: { color: 'transparent' },
        textColor: 'rgba(255,255,255,0.4)',
        fontSize: 10,
      },
      grid: {
        vertLines: { color: 'rgba(255,255,255,0.03)' },
        horzLines: { color: 'rgba(255,255,255,0.03)' },
      },
      rightPriceScale: {
        borderVisible: false,
        scaleMargins: { top: 0.05, bottom: 0.22 },
      },
      timeScale: {
        borderVisible: false,
        visible: false,
      },
      crosshair: {
        vertLine: { visible: false },
        horzLine: { visible: false },
      },
      handleScroll: false,
      handleScale: false,
    });

    const precision = pricePrecision(lastPrice);
    const candle = chart.addSeries(CandlestickSeries, {
      upColor: '#34d399',
      downColor: '#ef5350',
      borderVisible: false,
      wickUpColor: '#34d399',
      wickDownColor: '#ef5350',
      priceFormat: { type: 'price', precision, minMove: 1 / Math.pow(10, precision) },
    });

    const vol = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: 'volume',
    } as any);
    chart.priceScale('volume').applyOptions({
      scaleMargins: { top: 0.85, bottom: 0 },
    } as any);

    chartRef.current = chart;
    candleRef.current = candle;
    volRef.current = vol;

    const ro = new ResizeObserver((entries) => {
      if (chartRef.current) {
        chartRef.current.applyOptions({ width: entries[0].contentRect.width });
      }
    });
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      candleRef.current = null;
      volRef.current = null;
    };
  }, []); // mount-only

  // Update data when klines/signal/indicators change (no chart destroy)
  useEffect(() => {
    if (!candleRef.current || !volRef.current || !chartRef.current) return;
    const valid = sanitizeKlines(klines || []);
    if (valid.length === 0) return;

    try {
      candleRef.current.setData(valid as any);

      const volumeSMA = indicators?.volume_sma;
      const volumeData = valid.map(k => ({
        time: k.time as any,
        value: k.volume,
        color: volumeSMA && k.volume > volumeSMA * 3
          ? 'rgba(251, 191, 36, 0.6)'
          : 'rgba(99, 102, 241, 0.25)',
      }));
      volRef.current.setData(volumeData as any);

      // Only fitContent on first data — prevents flicker on 2s updates
      if (!fittedRef.current) {
        chartRef.current.timeScale().fitContent();
        fittedRef.current = true;
      }

      if (signal) {
        (candleRef.current as any).setMarkers([{
          time: (signal.timestamp || valid[valid.length - 1].time) as any,
          position: signal.direction === 'LONG' ? 'belowBar' : 'aboveBar',
          color: signal.direction === 'LONG' ? '#34d399' : '#ef5350',
          shape: signal.direction === 'LONG' ? 'arrowUp' : 'arrowDown',
          text: signal.direction === 'LONG' ? 'L' : 'S',
        }]);
      } else {
        (candleRef.current as any).setMarkers([]);
      }
    } catch {
      // Ignore lightweight-charts internal rendering errors
    }
  }, [klines, signal, indicators]);

  const displaySymbol = symbol.replace('USDT', '');
  const priceDig = pricePrecision(lastPrice);
  const priceStr = lastPrice.toLocaleString('en-US', { minimumFractionDigits: priceDig, maximumFractionDigits: priceDig });

  const phaseLabel = phase || 'idle';

  return (
    <div className="mini-chart-card" onClick={onClick}>
      <div className="mini-chart-header">
        <div className="mini-chart-symbol-group">
          <span className="mini-chart-symbol">{displaySymbol}</span>
          {squeezeScore !== undefined && squeezeScore > 0 && (
            <span className={`squeeze-score score-${
              squeezeScore >= 80 ? 'high' : squeezeScore >= 60 ? 'mid' : squeezeScore >= 40 ? 'low' : 'none'
            }`}>{squeezeScore}</span>
          )}
        </div>
        <span className="mini-chart-price">${priceStr}</span>
      </div>
      <div className={`mini-chart-phase phase-${phaseLabel}`}>
        {phaseLabel.charAt(0).toUpperCase() + phaseLabel.slice(1)}
        {indicators?.last_rejection && (
          <span className="rejection-reason"> — {indicators.last_rejection}</span>
        )}
      </div>
      {scoreBreakdown && (
        <div className="score-breakdown">
          <span title="Bollinger Band squeeze">BB:{scoreBreakdown.bb_width}</span>
          <span title="Volume lull">VL:{scoreBreakdown.volume_lull}</span>
          <span title="ATR compression">AT:{scoreBreakdown.atr_comp}</span>
          <span title="OI growth">OI:{scoreBreakdown.oi_growth}</span>
          <span title="Funding rate">FR:{scoreBreakdown.funding}</span>
        </div>
      )}
      <div ref={containerRef} className="mini-chart-canvas" />
      {indicators && Object.keys(indicators).length > 0 && (
        <div className="mini-chart-indicators">
          {indicators.rsi !== undefined && (
            <span className="indicator-tag">RSI {indicators.rsi.toFixed(1)}</span>
          )}
          {indicators.atr !== undefined && (
            <span className="indicator-tag">ATR {indicators.atr.toFixed(2)}</span>
          )}
          {indicators.volume_sma !== undefined && (
            <span className="indicator-tag">Vol {formatCompact(indicators.volume_sma)}</span>
          )}
          {indicators.cvd !== undefined && (
            <span className="indicator-tag">CVD {indicators.cvd.toFixed(1)}</span>
          )}
          {indicators.funding_rate !== undefined && (
            <span className="indicator-tag">FR {(indicators.funding_rate * 100).toFixed(4)}%</span>
          )}
        </div>
      )}
      {signal && (
        <div className={`mini-chart-signal ${signal.direction === 'LONG' ? 'signal-long' : 'signal-short'}`}>
          {signal.direction === 'LONG' ? '↑ LONG' : '↓ SHORT'} ({(signal.confidence * 100).toFixed(0)}%)
        </div>
      )}
    </div>
  );
};

export default MiniChart;
